/**
 * @file color-roles.ts
 * @description Подсказки по цветам ролей для экрана «Оформление» (phase19.16
 * Р1; SB9; uiux-design-r1 §1 «Контраст», окно цвета): метка контраста роли,
 * ближайший читаемый цвет, текст на кнопке и строки «Текст на кнопке —
 * белый, контраст 7,3 ✓».
 *
 * Расчёт — пакет shared (`evaluateSiteContrast`, `nearestReadableForRole`,
 * `pickButtonText`); здесь только сборка того, что видит владелец, и защита
 * от недописанного цвета (поле HEX во время ввода).
 */

import {
  evaluateSiteContrast,
  formatContrast,
  nearestReadableButton,
  nearestReadableForRole,
  parseHexColor,
  pickButtonText,
  SITE_COLOR_PATTERN,
  SITE_COLOR_ROLES,
  SITE_CONTRAST_LEVEL_MARKS,
  type SiteColorRole,
  type SiteColors,
  type SiteContrastLevel,
} from "@chaiberi/shared";

/** Метка контраста роли. */
export interface RoleContrastView {
  level: SiteContrastLevel;
  ratioLabel: string;
  mark: string;
  /** Норма правила: «4,5» или «3». */
  minLabel: string;
  /** Ближайший читаемый цвет и его контраст — только при «!» и «✕». */
  nearest: { hex: string; ratioLabel: string } | null;
}

/** Все цвета ролей — валидные hex (во время ввода может быть недописанный). */
export function colorsValid(colors: SiteColors): boolean {
  return SITE_COLOR_ROLES.every((role) => SITE_COLOR_PATTERN.test(colors[role]));
}

/** Метка контраста роли; `null` — роль-фон без проверки или цвета неполные. */
export function roleContrast(colors: SiteColors, role: SiteColorRole): RoleContrastView | null {
  if (!colorsValid(colors)) return null;
  const report = evaluateSiteContrast(colors);
  const item = report.roles.find((r) => r.role === role);
  if (!item) return null;
  let nearest: RoleContrastView["nearest"] = null;
  if (item.level !== "ok") {
    const hex = nearestReadableForRole(colors, role);
    if (hex) {
      const next = evaluateSiteContrast({ ...colors, [role]: hex }).roles.find((r) => r.role === role);
      nearest = { hex, ratioLabel: next?.ratioLabel ?? "" };
    }
  }
  return {
    level: item.level,
    ratioLabel: item.ratioLabel,
    mark: SITE_CONTRAST_LEVEL_MARKS[item.level],
    minLabel: formatContrast(item.min),
    nearest,
  };
}

/** Текст на кнопке для строки роли «Кнопки». */
export interface ButtonTextView {
  toneLabel: "белый" | "тёмный";
  ratioLabel: string;
  mark: string;
  level: SiteContrastLevel;
  /** Ни белый, ни тёмный не читаются — ближайший цвет кнопки, где читается. */
  nearestButton: string | null;
}

export function buttonTextView(colors: SiteColors): ButtonTextView | null {
  if (!colorsValid(colors)) return null;
  const text = pickButtonText(colors);
  return {
    toneLabel: text.tone === "light" ? "белый" : "тёмный",
    ratioLabel: text.ratioLabel,
    mark: SITE_CONTRAST_LEVEL_MARKS[text.level],
    level: text.level,
    nearestButton: text.level === "ok" ? null : nearestReadableButton(colors),
  };
}

/** Короткое пояснение уровня для строки роли. */
export const CONTRAST_LEVEL_HINT: Readonly<Record<SiteContrastLevel, string>> = {
  ok: "читается",
  warn: "только для крупного текста",
  fail: "трудно читать",
};

/** Цвет текста на образце цвета (чтобы HEX был виден на любом образце). */
export function inkOn(hex: string): string {
  const parsed = parseHexColor(hex);
  if (!parsed) return "#000000";
  const luminance = (0.299 * parsed.r + 0.587 * parsed.g + 0.114 * parsed.b) / 255;
  return luminance > 0.6 ? "#111111" : "#ffffff";
}
